import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Chart } from "chart.js/auto";

// ===================== helpers =====================
const fmt = (n) => new Intl.NumberFormat("es-CL").format(n || 0);
const MONTH_YYYY_MM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// ===================== componente =====================
const DashboardAlmuerzos = () => {
  const [month, setMonth] = useState(MONTH_YYYY_MM());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
const [topNoShow, setTopNoShow] = useState([]);
const [detalleOpen, setDetalleOpen] = useState(false);
const [detalleLoading, setDetalleLoading] = useState(false);
const [detallePersona, setDetallePersona] = useState(null); // {rut, nombre}
const [detalleRows, setDetalleRows] = useState([]);         // [{fecha, almuerzo}]
const [detalleUntil, setDetalleUntil] = useState('');
  // datos del backend
  const [ult7, setUlt7] = useState([]);             // [{fecha,total}] — emitidos reales (registros)
  const [cruceMes, setCruceMes] = useState([]);     // [{fecha, prog, emitidos, noshow, por_tipo: {tipo: {prog, emit}}}]
  const [pronostico, setPronostico] = useState({ days: [], by_type: [] }); // opcional/compat

  // precios (persisten en localStorage)
  const defaultPrices = useMemo(() => ({
    "Hipocalórico": 0,
    "Vegetariano": 0,
    "Fondo 1": 0,
    "Fondo 2": 0,
    "Otros": 0
  }), []);

  const [prices, setPrices] = useState(() => {
    const saved = localStorage.getItem("precio_almu_tipos");
    return saved ? JSON.parse(saved) : {
      "Hipocalórico": 0,
      "Vegetariano": 0,
      "Fondo 1": 0,
      "Fondo 2": 0,
      "Otros": 0
    };
  });

  useEffect(() => {
    localStorage.setItem("precio_almu_tipos", JSON.stringify(prices));
  }, [prices]);

  // chart refs
  const lineRef = useRef(null);
  const lineChart = useRef(null);

  // ----------------- fetch -----------------
  const loadData = async () => {
    setLoading(true);
    setErr("");
    try {
      const [r7, rCruce, rMes, rTop] = await Promise.all([
        axios.get("https://casinop2.sanjoseapps.cl/api/ultimos7_tickets.php"),
        axios.get("https://casinop2.sanjoseapps.cl/api/cruce_mes_programado_vs_emitido.php", { params: { month } }),
        axios.get("https://casinop2.sanjoseapps.cl/api/get_pronostico_mes.php", { params: { month } }), // opcional
        axios.get("https://casinop2.sanjoseapps.cl/api/top_noshow_mes.php", { params: { month, limit: 100 } })
  
      ]);
      setUlt7(r7.data?.data || []);
      setCruceMes(rCruce.data?.data || []);
      setPronostico({
        days: rMes.data?.days || [],
        by_type: rMes.data?.by_type || []
      });
       setTopNoShow(rTop.data?.data || []);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "No se pudo cargar el dashboard.");
    } finally {
      setLoading(false);
    }
  };
const abrirDetalleNoShow = async (persona) => {
  // persona = { rut, nombre, ... }
  setDetallePersona({ rut: persona.rut, nombre: persona.nombre });
  setDetalleOpen(true);
  setDetalleLoading(true);
  try {
    const resp = await axios.get("https://casinop2.sanjoseapps.cl/api/detalle_noshow_persona.php", {
      params: { month, rut: persona.rut }
    });
    if (resp.data?.ok) {
      setDetalleRows(resp.data.data || []);
      setDetalleUntil(resp.data.until || "");
    } else {
      setDetalleRows([]);
    }
  } catch (e) {
    setDetalleRows([]);
  } finally {
    setDetalleLoading(false);
  }
};

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [month]);

  // ----------------- métricas (mes) -----------------
  const totalProgramadosMes = useMemo(
    () => cruceMes.reduce((acc, d) => acc + (d.prog || 0), 0),
    [cruceMes]
  );
  const totalEmitidosMes = useMemo(
    () => cruceMes.reduce((acc, d) => acc + (d.emitidos || 0), 0),
    [cruceMes]
  );
  const totalNoShowMes = useMemo(
    () => Math.max(0, totalProgramadosMes - totalEmitidosMes),
    [totalProgramadosMes, totalEmitidosMes]
  );

  
  // Totales por tipo (PROGRAMADO) agregados en el mes
  const totalesPorTipoProg = useMemo(() => {
    const m = { "Hipocalórico":0, "Vegetariano":0, "Fondo 1":0, "Fondo 2":0, "Otros":0 };
    cruceMes.forEach(d => {
      const pt = d.por_tipo || {};
      Object.keys(pt).forEach(k => { m[k] = (m[k] || 0) + (pt[k]?.prog || 0); });
    });
    return m;
  }, [cruceMes]);

  // Totales por tipo (EMITIDOS) agregados en el mes
  const totalesPorTipoEmit = useMemo(() => {
    const m = { "Hipocalórico":0, "Vegetariano":0, "Fondo 1":0, "Fondo 2":0, "Otros":0 };
    cruceMes.forEach(d => {
      const pt = d.por_tipo || {};
      Object.keys(pt).forEach(k => { m[k] = (m[k] || 0) + (pt[k]?.emit || 0); });
    });
    return m;
  }, [cruceMes]);

  // Totales por tipo (NO-SHOW) = prog - emit (no negativos), sumados en el mes
  const totalesPorTipoNoShow = useMemo(() => {
    const m = { "Hipocalórico":0, "Vegetariano":0, "Fondo 1":0, "Fondo 2":0, "Otros":0 };
    cruceMes.forEach(d => {
      const pt = d.por_tipo || {};
      Object.keys(pt).forEach(k => {
        const prog = pt[k]?.prog || 0;
        const emit = pt[k]?.emit || 0;
        const ns = Math.max(0, prog - emit);
        m[k] = (m[k] || 0) + ns;
      });
    });
    return m;
  }, [cruceMes]);

  // Costos del mes (según precios)
  const costoProgramado = useMemo(() => {
    return Object.keys(totalesPorTipoProg).reduce((sum, tipo) =>
      sum + (totalesPorTipoProg[tipo]||0) * (Number(prices[tipo]||0)), 0);
  }, [totalesPorTipoProg, prices]);

  const costoEmitido = useMemo(() => {
    return Object.keys(totalesPorTipoEmit).reduce((sum, tipo) =>
      sum + (totalesPorTipoEmit[tipo]||0) * (Number(prices[tipo]||0)), 0);
  }, [totalesPorTipoEmit, prices]);

  const costoNoShow = useMemo(() => {
    return Object.keys(totalesPorTipoNoShow).reduce((sum, tipo) =>
      sum + (totalesPorTipoNoShow[tipo]||0) * (Number(prices[tipo]||0)), 0);
  }, [totalesPorTipoNoShow, prices]);

  // Más / menos solicitado (según PROGRAMADO en el mes)
  const masSolicitado = useMemo(() => {
    const arr = Object.entries(totalesPorTipoProg);
    if (!arr.length) return null;
    return arr.reduce((mx, cur) => (cur[1] > mx[1] ? cur : mx), ["", 0]);
  }, [totalesPorTipoProg]);
  const menosSolicitado = useMemo(() => {
    const arr = Object.entries(totalesPorTipoProg);
    if (!arr.length) return null;
    return arr.reduce((mn, cur) => (cur[1] < mn[1] ? cur : mn), ["", Infinity]);
  }, [totalesPorTipoProg]);

  // ----------------- línea "últimos 7 días" -----------------
  useEffect(() => {
    if (lineChart.current) { lineChart.current.destroy(); lineChart.current = null; }
    if (lineRef.current && ult7.length) {
      lineChart.current = new Chart(lineRef.current, {
        type: "line",
        data: {
          labels: ult7.map(x => x.fecha),
          datasets: [{
            label: "Tickets (registros) últimos 7 días",
            data: ult7.map(x => x.total),
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          elements: { point: { radius: 3 } },
          plugins: {
            legend: { display: true },
            tooltip: { callbacks: { label: (ctx) => `${fmt(ctx.raw)} tickets` } }
          },
          scales: {
            x: { ticks: { maxRotation: 0 }, title: { display: true, text: "Fecha" } },
            y: { beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: "Tickets" } }
          }
        }
      });
    }
  }, [ult7]);

  // ----------------- UI -----------------
  return (
    <>
      <Navbar />
      <section className="section">
        <div className="container">

          <h1 className="title" style={{ fontSize: 28, fontWeight: 800 }}>Dashboard</h1>

          {/* KPIs superiores */}
          <div className="columns is-multiline">
            <div className="column is-3">
              <div className="box" style={{ borderRadius: 16 }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Programados (mes)</div>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{fmt(totalProgramadosMes)}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Más solicitado: {masSolicitado ? `${masSolicitado[0]} (${fmt(masSolicitado[1])})` : "-"}</div>
              </div>
            </div>
            <div className="column is-3">
              <div className="box" style={{ borderRadius: 16 }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Emitidos (mes)</div>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{fmt(totalEmitidosMes)}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Costo emitido: ${fmt(costoEmitido)}</div>
              </div>
            </div>
            <div className="column is-3">
              <div className="box" style={{ borderRadius: 16 }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>No Almorzaron (mes)</div>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{fmt(totalNoShowMes)}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Costo: ${fmt(costoNoShow)}</div>
              </div>
            </div>
            <div className="column is-3">
              <div className="box" style={{ borderRadius: 16 }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Mes seleccionado</div>
                <input
                  className="input"
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                />
                {loading && <div className="tag is-warning is-light" style={{ marginTop: 8 }}>Actualizando…</div>}
              </div>
            </div>
          </div>

          {/* Línea últimos 7 días */}
          <div className="box" style={{ borderRadius: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Gráfico: Tickets (registros) últimos 7 días</div>
            <div style={{ height: 320 }}>
              <canvas ref={lineRef} />
            </div>
          </div>

          {/* Precios y costos del mes */}
          <div className="box" style={{ borderRadius: 16 }}>
            <div className="columns">
              {/* Columna de precios por tipo */}
              <div className="column is-7">
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Precios por tipo</div>
                <div className="columns is-multiline">
                  {["Hipocalórico","Vegetariano","Fondo 1","Fondo 2","Otros"].map((t) => (
                    <div className="column is-4" key={t}>
                      <label className="label" style={{ fontSize: 12 }}>{t}</label>
                      <input
                        type="number"
                        min="0"
                        className="input"
                        value={prices[t] ?? 0}
                        onChange={(e) => setPrices((p) => ({ ...p, [t]: Number(e.target.value || 0) }))}
                      />
                      <p className="help">
                        Programados: {fmt(totalesPorTipoProg[t] || 0)} | Emitidos: {fmt(totalesPorTipoEmit[t] || 0)} | No vinieron: {fmt(totalesPorTipoNoShow[t] || 0)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Columna de costos resumidos */}
              <div className="column is-5">
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Costos del mes</div>

                {/* Recuadro: Programado */}
                <div className="box" style={{ borderRadius: 12, background: "#f9fafb", marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Costo Programado</div>
                  <div style={{ fontSize: 28, fontWeight: 800 }}>${fmt(costoProgramado)}</div>
                  <div className="content" style={{ marginTop: 8 }}>
                    <ul style={{ marginBottom: 0 }}>
                      {Object.keys(totalesPorTipoProg).map((t) => (
                        <li key={`prog-${t}`}>
                          {t}: {fmt(totalesPorTipoProg[t]||0)} × ${fmt(prices[t]||0)} = ${fmt((totalesPorTipoProg[t]||0)*(prices[t]||0))}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Recuadro: Emitido */}
                <div className="box" style={{ borderRadius: 12, background: "#f1f5f9", marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#475569" }}>Costo Emitido (real)</div>
                  <div style={{ fontSize: 28, fontWeight: 800 }}>${fmt(costoEmitido)}</div>
                  <div className="content" style={{ marginTop: 8 }}>
                    <ul style={{ marginBottom: 0 }}>
                      {Object.keys(totalesPorTipoEmit).map((t) => (
                        <li key={`emit-${t}`}>
                          {t}: {fmt(totalesPorTipoEmit[t]||0)} × ${fmt(prices[t]||0)} = ${fmt((totalesPorTipoEmit[t]||0)*(prices[t]||0))}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Recuadro: No-show */}
                <div className="box" style={{ borderRadius: 12, background: "#fff7ed" }}>
                  <div style={{ fontSize: 12, color: "#92400e" }}>Costo No Almorzaon (programado no consumido)</div>
                  <div style={{ fontSize: 28, fontWeight: 800 }}>${fmt(costoNoShow)}</div>
                  <div className="content" style={{ marginTop: 8 }}>
                    <ul style={{ marginBottom: 0 }}>
                      {Object.keys(totalesPorTipoNoShow).map((t) => (
                        <li key={`ns-${t}`}>
                          {t}: {fmt(totalesPorTipoNoShow[t]||0)} × ${fmt(prices[t]||0)} = ${fmt((totalesPorTipoNoShow[t]||0)*(prices[t]||0))}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Tabla de días del mes (programado vs emitido vs no-show) */}
          <div className="box" style={{ borderRadius: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>
              Días del mes — Programado vs. Almorzó (registros) y No Almorzó
            </div>
            {err && <div className="notification is-danger">{err}</div>}
            {loading && <div className="notification is-info is-light">Cargando…</div>}
            <div style={{ overflowX: "auto" }}>
              <table className="table is-fullwidth is-striped is-hoverable">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th className="has-text-right">Programados</th>
                    <th className="has-text-right">Emitidos</th>
                    <th className="has-text-right">No comidos</th>
                    <th className="has-text-right">Hipocalórico (emit)</th>
                    <th className="has-text-right">Vegetariano (emit)</th>
                    <th className="has-text-right">Fondo 1 (emit)</th>
                    <th className="has-text-right">Fondo 2 (emit)</th>
                    <th className="has-text-right">Otros (emit)</th>
                  </tr>
                </thead>
                <tbody>
                  {cruceMes.map((d) => {
                    const pt = d.por_tipo || {};
                    return (
                      <tr key={d.fecha}>
                        <td>{d.fecha}</td>
                        <td className="has-text-right">{fmt(d.prog)}</td>
                        <td className="has-text-right">{fmt(d.emitidos)}</td>
                        <td className="has-text-right">{fmt(d.noshow)}</td>
                        <td className="has-text-right">{fmt(pt["Hipocalórico"]?.emit || 0)}</td>
                        <td className="has-text-right">{fmt(pt["Vegetariano"]?.emit || 0)}</td>
                        <td className="has-text-right">{fmt(pt["Fondo 1"]?.emit || 0)}</td>
                        <td className="has-text-right">{fmt(pt["Fondo 2"]?.emit || 0)}</td>
                        <td className="has-text-right">{fmt(pt["Otros"]?.emit || 0)}</td>
                      </tr>
                    );
                  })}
                  {!cruceMes.length && !loading && (
                    <tr><td colSpan="9" className="has-text-centered">Sin datos para este mes</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

<div className="box" style={{ borderRadius: 16 }}>
  <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>
    Personas con más almuerzos desperdiciados del mes (solicitaron y no almorzaron)
  </div>
  <p className="help" style={{ marginTop: -6 }}>
    Ordenado de mayor a menor cantidad de perdida de almuerzos. Se basa en la programación cruzada con registros.
  </p>
  <div style={{ overflowX: "auto" }}>
    <table className="table is-fullwidth is-striped is-hoverable">
      <thead>
        <tr>
          <th>#</th>
          <th>Nombre</th>
          <th>RUT</th>
          <th>Área</th>
          <th>Centro de Costo</th>
          <th className="has-text-right">Almuerzo desperdiciados (mes)</th>
        </tr>
      </thead>
      <tbody>
        {topNoShow.map((p, idx) => (
          <tr
  key={`${p.rut}-${idx}`}
  style={{ cursor: "pointer" }}
  onClick={() => abrirDetalleNoShow(p)}
>
            <td>{idx + 1}</td>
            <td>{p.nombre || "-"}</td>
            <td>{p.rut || "-"}</td>
            <td>{p.area || "-"}</td>
            <td>{p.centro_costo || "-"}</td>
            <td className="has-text-right">{fmt(p.noshow || 0)}</td>
          </tr>
        ))}
        {!topNoShow.length && !loading && (
          <tr><td colSpan="6" className="has-text-centered">Sin no-shows en este mes 🎉</td></tr>
        )}
        {loading && (
          <tr><td colSpan="6" className="has-text-centered">Cargando…</td></tr>
        )}
      </tbody>
    </table>
  </div>
</div>
{/* Modal detalle no-show por persona */}
<div className={`modal ${detalleOpen ? "is-active" : ""}`}>
  <div className="modal-background" onClick={() => setDetalleOpen(false)}></div>
  <div className="modal-card" style={{ width: "840px", maxWidth: "95vw" }}>
    <header className="modal-card-head">
      <p className="modal-card-title" style={{ fontWeight: 800, fontSize: 18 }}>
        No-show de {detallePersona?.nombre || ""} — {detallePersona?.rut || ""}
      </p>
      <button className="delete" aria-label="close" onClick={() => setDetalleOpen(false)}></button>
    </header>
    <section className="modal-card-body">
      <p className="help" style={{ marginBottom: 12 }}>
        Mostrando desde el 1 de {month} hasta <strong>{detalleUntil || "hoy"}</strong>.
      </p>

      {detalleLoading ? (
        <div className="notification is-info is-light">Cargando…</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="table is-fullwidth is-striped is-hoverable">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Menú programado</th>
              </tr>
            </thead>
            <tbody>
              {detalleRows.map((r, i) => (
                <tr key={i}>
                  <td>{r.fecha}</td>
                  <td>{r.almuerzo || "-"}</td>
                </tr>
              ))}
              {!detalleRows.length && (
                <tr><td colSpan="2" className="has-text-centered">Sin no-shows en el rango 🎉</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
    <footer className="modal-card-foot" style={{ justifyContent: "flex-end" }}>
      <button className="button" onClick={() => setDetalleOpen(false)}>Cerrar</button>
    </footer>
  </div>
</div>
        </div>
      </section>
    </>
  );
};

export default DashboardAlmuerzos;
